import { Link } from 'react-router-dom';
import { PATHS } from 'Utils/PagePaths';

export const HOSPITALV2_0_DATA = [
  {
    title: "Introduction",
    level: 1,
    content: "The Common Formats for Event Reporting – Hospital Version 2.0 (CFER-H V2.0) represents a major update to the AHRQ Common Formats for hospitals." +
      " Version 2.0 streamlines how hospitals capture and report patient safety events by condensing event descriptions," +
      " consolidating key forms into a single Generic module, and eliminating paper-based reporting." + 
      " This version distinguishes between core data elements, required for national aggregation and analysis," +
      " and supplemental data elements, which may be collected locally for additional insights.",
    cardsPrologue: "On this page, you'll find Hospital V2.0 resources including the Data Dictionary, Resources Workbook, Flow Charts, and Technical Release Notes." +
      " These materials support providers, PSOs, and developers in designing systems that collect and transmit standardized patient safety data for national improvement efforts.",
  },
  {
    title: "Collect Data",
    level: 1,
    content: <><strong>Collect Data from Providers</strong>: Prepare your patient safety event information using the Hospital Version 2.0 Common Format. The resources provided below including the Data Dictionary, Users' Guide, and Flow Charts, will help you understand required data elements, answer values, and validation rules before you create your submission files.</>,
    children: [
      {
        title: "Key Resources",
        level: 2,
        cards: [2626, 2628, 2631, 2602]
      },
      {
        title: "Event Details",
        level: 2,
        cards: [],
        children: [
          {
            title: "Generic",
            level: 3,
            content: "The Generic Module sets out the common information to be collected for all patient safety concerns, regardless of the event" +
              " type or unsafe condition. This category maps out the type of patient safety concern, the circumstances of the event or unsafe condition," +
              " patient information (if applicable), and reporter information.",
            cardsPrologue: "",
            cards: [2589, 2612, 2615, 2627, 2611]
          },
          {
            title: "Anesthesia",
            level: 3,
            mainContentTitle: "Anesthesia Event",
            content: "An Anesthesia event is associated with the administration" +
              " of anesthesia or sedation. Patient safety events for the Anesthesia" +
              " category include incidents and near misses. They also pertain to both" +
              " inpatient and outpatient procedures occurring in a hospital.",
            cardsPrologue: "",
            cards: [2593, 2597, 2596]
          },
          {
            title: "Blood or Blood Product",
            level: 3,
            mainContentTitle: "Blood or Blood Product Event",
            content: "A Blood or Blood Product event or unsafe condition involves the processing and/or administration of blood or a blood product." +
              " Patient safety concerns for this category include incidents, near-misses, and unsafe conditions that occur in a hospital and do not require that a patient outcome be identified.",
            cardsPrologue: "",
            cards: [2592, 2599, 2600]
          },
          {
            title: "Device or Medical/Surgical Supply",
            level: 3,
            mainContentTitle: "Device or Medical/Surgical Supply Event",
            content: "A Device or Medical/Surgical Supply event or unsafe condition involves a defect, failure, or incorrect use of a device. Patient safety concerns for this category include incidents," +
              " near misses, and unsafe conditions, and do not require that a patient outcome be identified.",
            cardsPrologue: "",
            cards: [2591, 2605, 2604]
          },
          {
            title: "Fall",
            level: 3,
            mainContentTitle: "Fall Event",
            content: "A Fall event is a sudden, unintended, descent of a patient's body to the ground or other object" +
              " (e.g., onto a bed, chair, or bedside mat) that can be assisted or unassisted. Patient safety concerns for the Fall category include only incidents that occur in a hospital.",
            cardsPrologue: "",
            cards: [2590, 2610, 2609]
          },
          {
            title: "Medication or Other Substance",
            level: 3,
            mainContentTitle: "Medication or Other Substance Event",
            content: "A Medication or Other Substance event involves the processing" +
              " and/or administration of medications, biological products, nutritional" +
              " substances, medical gases, contrast media, or radiopharmaceuticals." +
              " Patient safety concerns for this category include incidents, near misses," +
              " and unsafe conditions and do not require a patient outcome be identified." +
              " This category pertains to both inpatient and outpatient pharmacies located" +
              " within the hospital.",
            cardsPrologue: "",
            cards: [2588, 2613, 2614]
          },
          {
            title: "Perinatal",
            level: 3,
            mainContentTitle: "Perinatal Event",
            content: "A Perinatal event involves an adverse outcome occurring" +
              " to the mother, fetus(es), or neonate(s) associated with either the birthing process (labor, birth, or postpartum) or an intrauterine" +
              " procedure. Perinatal events include those that occur from the 20th week of gestation through 4 weeks (28 days) postpartum (up to 42 days for maternal events). Patient safety concerns for this category include only incidents that occur in a hospital.",
            cardsPrologue: "",
            cards: [2587, 2618, 2617]
          },
          {
            title: "Pressure Injury",
            level: 3,
            mainContentTitle: "Pressure Injury Event",
            content: "A Pressure Injury event involves a pressure injury of Stage" +
              " 2 or higher (i.e., Stage 2, 3, 4, Unstageable, or deep tissue pressure injury; mucosal membrane pressure injury; or secondary morbidity" +
              " attributed to the presence of a pressure injury) that develops in the hospital (i.e., was not present on admission) or worsens from a Stage 1" +
              " or Stage 2 pressure injury that was present on admission to the hospital." +
              " Patient safety concerns for this category include only incidents that occur in a hospital.",
            cardsPrologue: "",
            cards: [2586, 2621, 2620]
          },
          {
            title: "Surgery",
            level: 3,
            mainContentTitle: "Surgery Event",
            content: "A Surgical event is associated with an operating room or" +
              " other invasive procedure (e.g., colonoscopy, bone marrow biopsy)." +
              " Patient safety concerns for this event include incidents and near" +
              " misses. They also pertain to both inpatient and outpatient procedures" +
              " occurring in a hospital.",
            cardsPrologue: "",
            cards: [2585, 2625, 2624]
          },
          {
            title: "Venous Thromboembolism (VTE)",
            level: 3,
            mainContentTitle: "Venous Thromboembolism Event",
            content: "A Venous Thromboembolism (VTE) event comprises an objectively" +
              " confirmed symptomatic deep vein thrombosis (DVT) and/or pulmonary" +
              " embolism (PE). Patient safety concerns for the VTE category include" +
              " only incidents that occur in a hospital.",
            cardsPrologue: "",
            cards: [2584, 2583, 2594]
          }
        ]
      }
    ]
  },
  {
    title: "Submit Data",
    level: 1,
    content: <><strong>Submit Data to NPSD</strong>: Submit your patient safety event data to the PSOPPC using the Hospital Version 2.0 Common Format. These resources walk you through the process for both CDA XML and Alternate Data Submission formats, including an Implementation Guide, Resources Workbook, and Sample Files. For more information before you get started, see the <Link to={PATHS.public['Submit Data']}>Submit Data</Link> page.</>,
    children: [
      {
        title: "Key Resources",
        level: 2,
        cards: [2630, 2622, 2042, 2603, 2632, 2634, 2633]
      },
      {
        title: "Event Details",
        level: 2,
        cards: [],
        children: [
          {
            title: "Anesthesia",
            level: 3,
            mainContentTitle: "Anesthesia Event",
            content: "",
            cardsPrologue: "",
            cards: [2595]
          },
          {
            title: "Blood or Blood Product",
            level: 3,
            mainContentTitle: "Blood or Blood Product Event",
            content: "",
            cardsPrologue: "",
            cards: [2598]
          },
          {
            title: "Device or Medical/Surgical Supply",
            level: 3,
            mainContentTitle: "Device or Medical/Surgical Supply Event",
            content: "",
            cardsPrologue: "",
            cards: [2606, 2607]
          },
          {
            title: "Fall",
            level: 3,
            mainContentTitle: "Fall Event",
            content: "",
            cardsPrologue: "",
            cards: [2608]
          },
          {
            title: "Perinatal",
            level: 3,
            mainContentTitle: "Perinatal Event",
            content: "",
            cardsPrologue: "",
            cards: [2616]
          },
          {
            title: "Pressure Injury",
            level: 3,
            mainContentTitle: "Pressure Injury (Pressure Ulcer) Event",
            content: "",
            cardsPrologue: "",
            cards: [2619]
          },
          {
            title: "Surgery",
            level: 3,
            mainContentTitle: "Surgery Event",
            content: "",
            cardsPrologue: "",
            cards: [2623]
          },
          {
            title: "Venous Thromboembolism (VTE)",
            level: 3,
            mainContentTitle: "Venous Thromboembolism Event",
            content: "",
            cardsPrologue: "",
            cards: [2629]
          },
          {
            title: "Sample Files",
            level: 3,
            content: "",
            cardsPrologue: "",
            cards: [2601]
          },
        ]
      }
    ]
  }
]